import { makeAssistantToolUI } from '@assistant-ui/react'
import { GitMerge, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { percent } from '@/features/entity-resolution/groupFormat'
import { identityFieldKey } from '@/features/entity-resolution/identityAssessment'
import { ResolutionMergeProgress } from './ResolutionMergeProgress'
import {
  parseResolutionRuleJob,
  parseResolutionRulePlan,
  type ResolutionRulePlan,
} from './resolution-rule-data'
import { useResolutionRuleJob } from './useResolutionRuleJob'

interface ToolProps {
  result?: unknown
  artifact?: unknown
  status: { type: string }
}

export function ResolutionRulePlanSummary({ plan }: { plan: ResolutionRulePlan }) {
  const { t } = useTranslation()
  const fields = plan.rules.required_matching_fields.map(field => t(identityFieldKey(field)))
  return (
    <div className="space-y-2 text-sm">
      <p>
        {t('resolutionRules.scope', {
          entity: t(`resolutionRules.entityTypes.${plan.rules.entity_type ?? 'all'}`),
          min: percent(plan.rules.min_similarity),
          max: percent(plan.rules.max_similarity),
        })}
      </p>
      <p>
        {fields.length
          ? t('resolutionRules.requiredFields', { fields: fields.join(', ') })
          : t('resolutionRules.noRequiredFields')}
      </p>
      {plan.rules.same_source && <p>{t('resolutionRules.sameSource')}</p>}
      {plan.rules.max_similarity < 1 && (
        <p className="text-xs text-muted-foreground">{t('resolutionRules.rangeBasis')}</p>
      )}
      <p>{t('resolutionRules.selection', { ...plan })}</p>
      <p>{t('resolutionRules.remaining', { ...plan })}</p>
      <p className="text-xs text-muted-foreground">{t('resolutionRules.previewOnly')}</p>
    </div>
  )
}

export function ResolutionRulePlanCard({ result, artifact, status }: ToolProps) {
  const { caseId = '' } = useParams<{ caseId: string }>()
  const { t } = useTranslation()
  const plan = parseResolutionRulePlan(result, artifact, caseId)
  return (
    <Card className="my-2 gap-3 py-3">
      <CardHeader className="px-3">
        <CardTitle className="flex items-center gap-2 text-sm">
          <GitMerge aria-hidden className="size-4" />
          {t('resolutionRules.planTitle')}
        </CardTitle>
      </CardHeader>
      <CardContent className="px-3">
        {plan && status.type !== 'running' ? (
          <ResolutionRulePlanSummary plan={plan} />
        ) : (
          <RuleToolPlaceholder running={status.type === 'running'} />
        )}
      </CardContent>
    </Card>
  )
}

export function ResolutionRuleJobCard({ result, artifact, status }: ToolProps) {
  const { caseId = '' } = useParams<{ caseId: string }>()
  const { t } = useTranslation()
  const snapshot = parseResolutionRuleJob(result, artifact, caseId)
  return (
    <Card className="my-2 gap-3 py-3">
      <CardHeader className="px-3">
        <CardTitle className="text-sm">{t('resolutionRules.jobTitle')}</CardTitle>
      </CardHeader>
      <CardContent className="px-3">
        {snapshot && status.type !== 'running' ? (
          <RuleJobProgress snapshot={snapshot} />
        ) : (
          <RuleToolPlaceholder running={status.type === 'running'} />
        )}
      </CardContent>
    </Card>
  )
}

function RuleJobProgress({
  snapshot,
}: {
  snapshot: NonNullable<ReturnType<typeof parseResolutionRuleJob>>
}) {
  const job = useResolutionRuleJob(snapshot)
  return (
    <ResolutionMergeProgress
      job={job}
      retry={() => {
        void job.refetch()
      }}
    />
  )
}

function RuleToolPlaceholder({ running }: { running: boolean }) {
  const { t } = useTranslation()
  return running ? (
    <p role="status" className="flex items-center gap-2 text-sm">
      <Loader2 aria-hidden className="size-4 animate-spin" />
      {t('resolutionRules.loading')}
    </p>
  ) : (
    <p role="alert" className="text-sm text-destructive">
      {t('resolutionRules.unavailable')}
    </p>
  )
}

export const PlanEntityResolutionMergeToolUI = makeAssistantToolUI<unknown, unknown>({
  toolName: 'plan_entity_resolution_merge',
  render: ResolutionRulePlanCard,
})
export const MergeEntityResolutionToolUI = makeAssistantToolUI<unknown, unknown>({
  toolName: 'merge_entity_resolution',
  render: ResolutionRuleJobCard,
})
export const GetEntityResolutionMergeStatusToolUI = makeAssistantToolUI<unknown, unknown>({
  toolName: 'get_entity_resolution_merge_status',
  render: ResolutionRuleJobCard,
})
